// The standalone Parquet viewer page (/parquet-viewer). One full-width viewer,
// no second pane and no Compare button — the comparison tool keeps that job.
//
// The prose below is ALSO pre-rendered statically in parquet-viewer.html for
// crawlers that do not run JavaScript; tests/seo.test.js asserts the h1, lead,
// section headings and every FAQ question/answer stay in sync, so edit both or
// the build fails (G52).
import { INPUT_LIMIT_SINGLE_HELP } from '../lib/input-limits.js';
import { FILE_ICON, FOLDER_ICON } from './icons.js';
import { DATASET_LIMIT_HELP } from '../lib/dataset-limits.js';

export const VIEWER_H1 = 'Parquet viewer';
export const VIEWER_LEAD =
  'Open Parquet files and datasets locally. Inspect rows and schema, filter data, and export JSON or CSV.';

export const VIEWER_SECTIONS = [
  {
    h2: 'What the viewer shows',
    body: [
      'Every part of the file is decoded in this tab: the column schema with logical types and repetition, the row-group count, and the compression codecs actually used. Row values are shown exactly as stored — 64-bit integers and decimals stay exact strings instead of losing precision to a JavaScript number, binary columns are shown as hexadecimal, timestamps keep their original unit, and nulls are shown as null rather than as an empty cell.',
      'The filter box searches every column of every row, not just the page on screen. Rows are paginated 50 at a time so a large file stays responsive, and the JSON and CSV download buttons write out every matching row — all of them when no filter is active, and exactly the filtered set when one is.',
    ],
  },
  {
    h2: 'Files, parts and partitioned folders',
    body: [
      'A Parquet table is often many files. Select several .parquet parts, or open a whole folder, and the viewer validates every part and assembles them into one table — with the included files, their row counts and their sizes listed so you can see what was read. Non-Parquet files found in a folder, such as _SUCCESS or .crc marker files, are listed explicitly as excluded rather than quietly ignored.',
      'Hive-style folder segments such as year=2026/month=09 become real columns when the files themselves do not carry them. Percent escapes are decoded, leading zeroes are preserved as strings, and __HIVE_DEFAULT_PARTITION__ reads as null. If any part is corrupt, its schema is incompatible, or a partition value conflicts, the whole selection is rejected with the offending path named. You never get a silently truncated table.',
    ],
  },
  {
    h2: 'Limits',
    body: [
      `The viewer holds one dataset at a time. ${INPUT_LIMIT_SINGLE_HELP}`,
      `${DATASET_LIMIT_HELP} Rows are decoded and held in memory, so these ceilings are checked before anything is read: a selection over the limit is refused outright instead of being cut down to fit.`,
    ],
  },
  {
    h2: 'Nothing is uploaded',
    body: [
      'There is no server side to this page. The file never leaves your device: it is decoded by a Web Worker in this tab, and the Content-Security-Policy on this site allows connections to no origin but its own, so no script here could send your data anywhere even if it tried. You can check that in DevTools, or simply turn your wifi off and open a file anyway — the page is cached for offline use after your first visit.',
    ],
  },
];

export const VIEWER_FAQ = [
  {
    q: 'Does opening a Parquet file here upload it?',
    a: 'No. The file is read and decoded entirely in your browser tab by a Web Worker. No part of it is sent to a server, and the site loads no third-party scripts.',
  },
  {
    q: 'Can it open a folder of Parquet parts?',
    a: 'Yes. Choose Open Parquet folder, or drop a directory onto the page. Subfolders are discovered recursively, every part is validated before decoding, and Hive-style partition folders such as year=2026 become columns.',
  },
  {
    q: 'How large a file can it open?',
    a: 'Up to 25 MB of selected files, 256 parts, 256 MiB of uncompressed data, 500,000 rows and 5 million cells. Larger selections are refused with a clear message rather than partly loaded.',
  },
  {
    q: 'Are big integers and decimals shown exactly?',
    a: 'Yes. INT64 and DECIMAL values are kept as exact strings, binary values are shown as hexadecimal, and timestamps retain their stored precision, so nothing is rounded on the way to the screen.',
  },
  {
    q: 'Can I compare two Parquet datasets?',
    a: 'Yes, on the main cruxdiff comparison tool. It matches records by a key column you choose, reports added, removed and modified rows, and treats file names, part counts and row order as noise rather than changes.',
  },
];

export function parquetViewerPage() {
  return `
  <main class="wrap viewer-page">
    <!-- The first screen is the upload and nothing else: heading, one line of
         orientation, the drop target, and the privacy proof. Limits, the route to
         the comparison tool, the explainer and the FAQ all wait below the fold. -->
    <section class="viewer-hero">
      <h1>${VIEWER_H1}</h1>
      <p class="lead">${VIEWER_LEAD}</p>

      <section class="viewer-tool" aria-labelledby="viewerToolHeading">
        <h2 class="sr-only" id="viewerToolHeading">Open a Parquet file or dataset</h2>
        <div class="viewer-drop" id="viewerDrop">
          <div class="viewer-actions">
            <button class="upload-btn" type="button" data-input="viewerFileInput">${FILE_ICON}Open Parquet files</button>
            <button class="upload-btn" type="button" data-input="viewerFolderInput">${FOLDER_ICON}Open Parquet folder</button>
            <button class="clear-file-btn" type="button" id="viewerClear" hidden>Clear</button>
            <span class="fname" id="viewerFile"></span>
          </div>
          <input type="file" id="viewerFileInput" class="file-input" multiple accept=".parquet" aria-label="Open Parquet files" hidden>
          <input type="file" id="viewerFolderInput" webkitdirectory multiple aria-label="Open a Parquet folder" hidden>
          <p class="drop-copy">or drop a <code>.parquet</code> file, several parts, or a folder here</p>
        </div>
        <p class="upload-error loading" id="viewerError" role="status" hidden></p>
        <div class="file-viewer" id="viewerMount" aria-label="Parquet file viewer" hidden></div>
      </section>

      <p class="viewer-proof">
        <span class="proof">&#10003; <b>nothing uploaded</b></span>
        <span class="proof">&#10003; decoded <b>in this tab</b></span>
        <span class="proof">&#10003; works with <b>wifi off</b></span>
      </p>
      <p class="viewer-more"><a href="#viewer-about">What the viewer shows &#8595;</a></p>
    </section>

    <p class="viewer-secondary">Comparing two versions instead? <a href="/">Open the comparison tool</a> — it has its own two panes, so a file opened here is not carried over.</p>

    <section class="viewer-explainer" id="viewer-about">
      ${VIEWER_SECTIONS.map((s) => `<h2>${s.h2}</h2>${s.body.map((p) => `<p>${p}</p>`).join('')}`).join('')}
    </section>

    <section class="viewer-faq">
      <h2>Questions</h2>
      ${VIEWER_FAQ.map((f) => `<h3>${f.q}</h3><p>${f.a}</p>`).join('')}
      <p class="back"><a href="/go/">All comparison tools</a> · <a href="/privacy.html">How the privacy claim is enforced</a></p>
    </section>
  </main>`;
}
